import { EmptyState } from '@/components/empty-state';
import { NewExperienceDialog } from '@/components/experiences/new-experience-dialog';
import { PageHeader } from '@/components/shell/page-header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCampaigns, useContextTree, useExperiences, useSegments, type Campaign, type Experience } from '@/lib/queries';
import { useAuthStore } from '@/stores/auth';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRight, BarChart3, Boxes, CircleCheck, Layers3, TriangleAlert, Users } from 'lucide-react';
import { NewCampaignDialog, StatusBadge } from './campaigns.index';

export const Route = createFileRoute('/_authed/home')({ component: HomePage });

const SURFACE: Record<Experience['type'], string> = {
  modal: 'Modal',
  fullscreen: 'Fullscreen',
  banner: 'Banner',
  bottom_sheet: 'Bottom sheet',
  inline: 'Inline',
};

function Metric({ label, value, icon: Icon }: { label: string; value: number; icon: typeof Boxes }): React.JSX.Element {
  return (
    <div className="flex items-center gap-3 px-1 py-4 sm:px-4">
      <span className="grid size-9 shrink-0 place-items-center rounded-md border bg-background text-muted-foreground">
        <Icon className="size-4" />
      </span>
      <div>
        <p className="text-xl font-semibold tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function HomePage(): React.JSX.Element {
  const { appId, environmentId } = useContextStore();
  const user = useAuthStore((state) => state.user);
  const { data: context } = useContextTree();
  const { data: campaigns, isLoading: campaignsLoading } = useCampaigns(appId);
  const { data: experiences, isLoading: experiencesLoading } = useExperiences(appId);
  const { data: segments, isLoading: segmentsLoading } = useSegments(appId);

  const currentApp = context
    ?.flatMap((org) => org.workspaces)
    .flatMap((workspace) => workspace.apps)
    .find((app) => app.id === appId);
  const environment = currentApp?.environments.find((candidate) => candidate.id === environmentId);
  const rows = campaigns?.filter((campaign) => campaign.environment_id === environmentId) ?? [];
  const active = rows.filter((campaign) => campaign.status === 'active');
  const drafts = rows.filter((campaign) => campaign.status === 'draft');
  const recent = [...(experiences ?? [])]
    .sort((left, right) => Date.parse(right.updated_at) - Date.parse(left.updated_at))
    .slice(0, 5);
  const incomplete = drafts.filter((campaign) => !campaign.experience_id || !campaign.segment_id);
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const loading = campaignsLoading || experiencesLoading || segmentsLoading;

  // FIRST RUN. A freshly signed-up user has an org (auto-provisioned) but no app, so every metric and
  // list below is empty and the "create an experience" actions 400 for want of an app_id. Send them to
  // Install to create their first app instead of showing a dashboard of zeroes.
  const hasAnyApp = context?.some((org) => org.workspaces.some((workspace) => workspace.apps.length > 0));
  if (context !== undefined && !hasAnyApp) {
    return (
      <div className="space-y-8">
        <PageHeader title={firstName ? `Welcome, ${firstName}` : 'Welcome'} description="Let’s get your first app connected." />
        <EmptyState
          title="Create your first app"
          description="An app is where your experiences get delivered. Create one to get your SDK key and an install guide."
          action={
            <Button asChild>
              <Link to="/install">Get started</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={firstName ? `Welcome back, ${firstName}` : 'Workspace'}
        description={`Plan, build, and monitor mobile experiences for ${currentApp?.name ?? 'this app'}.`}
        actions={
          <>
            <NewCampaignDialog compact />
            <NewExperienceDialog compact />
          </>
        }
      />

      {loading ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : (
        <section aria-label="Workspace overview" className="grid grid-cols-2 divide-x border-y sm:grid-cols-4">
          <Metric label="Active campaigns" value={active.length} icon={Boxes} />
          <Metric label="Draft campaigns" value={drafts.length} icon={Layers3} />
          <Metric label="Experiences" value={experiences?.length ?? 0} icon={CircleCheck} />
          <Metric label="Audiences" value={segments?.length ?? 0} icon={Users} />
        </section>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.8fr)]">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Continue working</h2>
              <p className="text-xs text-muted-foreground">Recently edited experiences</p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/experiences">
                View all <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border">
            {experiencesLoading ? (
              <Skeleton className="h-56 rounded-none" />
            ) : recent.length ? (
              <div className="divide-y">
                {recent.map((experience) => (
                  <Link
                    key={experience.id}
                    to="/experiences/$id/edit"
                    params={{ id: experience.id }}
                    className="group flex items-center gap-3 px-4 py-3 hover:bg-secondary/50"
                  >
                    <span className="grid size-9 shrink-0 place-items-center rounded-md bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                      <Layers3 className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{experience.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {SURFACE[experience.type] ?? experience.type} · Version {experience.version}
                      </span>
                    </span>
                    <span className="hidden text-xs text-muted-foreground sm:block">
                      {new Date(experience.updated_at).toLocaleDateString()}
                    </span>
                    <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
                <Layers3 className="mb-3 size-5 text-muted-foreground" />
                <p className="text-sm font-medium">Create your first experience</p>
                <p className="mt-1 max-w-sm text-xs leading-5 text-muted-foreground">
                  Start with a mobile surface and refine it in the visual builder.
                </p>
                <div className="mt-4"><NewExperienceDialog compact /></div>
              </div>
            )}
          </div>
        </section>

        <section>
          <div className="mb-3">
            <h2 className="text-sm font-semibold">Needs attention</h2>
            <p className="text-xs text-muted-foreground">Work that may block delivery</p>
          </div>
          <div className="rounded-lg border">
            {!environment?.sdk_enabled ? (
              <AttentionRow
                icon={TriangleAlert}
                title={`${environment?.name ?? 'This environment'} delivery is off`}
                description="Enable the SDK in Settings before publishing."
                to="/settings"
              />
            ) : null}
            {incomplete.slice(0, 3).map((campaign) => (
              <AttentionRow
                key={campaign.id}
                icon={Boxes}
                title={campaign.name}
                description={!campaign.experience_id ? 'Needs an experience' : 'Needs an audience'}
                to="/campaigns/$id"
                params={{ id: campaign.id }}
              />
            ))}
            {environment?.sdk_enabled && incomplete.length === 0 ? (
              <div className="flex min-h-40 flex-col items-center justify-center px-5 text-center">
                <CircleCheck className="mb-2 size-5 text-emerald-600" />
                <p className="text-sm font-medium">Nothing is blocked</p>
                <p className="mt-1 text-xs text-muted-foreground">Drafts have the links they need.</p>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Live now</h2>
              <p className="text-xs text-muted-foreground">Active in {environment?.name ?? 'this environment'}</p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/campaigns">Campaigns <ArrowRight className="size-3.5" /></Link>
            </Button>
          </div>
          <div className="min-h-48 overflow-hidden rounded-lg border">
            {active.length ? active.slice(0, 4).map((campaign) => <CampaignRow key={campaign.id} campaign={campaign} />) : (
              <div className="flex min-h-48 flex-col items-center justify-center px-5 text-center">
                <Boxes className="mb-2 size-5 text-muted-foreground" />
                <p className="text-sm font-medium">No active campaigns</p>
                <p className="mt-1 text-xs text-muted-foreground">Publish a complete draft when it is ready.</p>
              </div>
            )}
          </div>
        </section>

        <section>
          <div className="mb-3">
            <h2 className="text-sm font-semibold">Recent analytics</h2>
            <p className="text-xs text-muted-foreground">Performance and response trends</p>
          </div>
          <div className="flex min-h-48 items-center gap-4 rounded-lg border border-dashed px-5">
            <span className="grid size-10 shrink-0 place-items-center rounded-md bg-sky-500/10 text-sky-600">
              <BarChart3 className="size-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Analytics workspace</p>
                <span className="rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase text-muted-foreground">Planned</span>
              </div>
              <p className="mt-1 max-w-md text-xs leading-5 text-muted-foreground">
                Delivery, impression, and action reporting will appear here once the analytics pipeline is connected.
              </p>
              <Button variant="link" size="sm" className="mt-1 h-auto px-0 text-xs" asChild>
                <Link to="/analytics">View planned workspace <ArrowRight className="size-3.5" /></Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function AttentionRow({
  icon: Icon,
  title,
  description,
  to,
  params,
}: {
  icon: typeof Boxes;
  title: string;
  description: string;
  to: '/settings' | '/campaigns/$id';
  params?: { id: string };
}): React.JSX.Element {
  const content = (
    <>
      <Icon className="mt-0.5 size-4 shrink-0 text-amber-600" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
      <ArrowRight className="size-4 text-muted-foreground" />
    </>
  );
  return to === '/settings' ? (
    <Link to="/settings" className="flex gap-3 border-b px-4 py-3 last:border-0 hover:bg-secondary/50">{content}</Link>
  ) : (
    <Link to="/campaigns/$id" params={params!} className="flex gap-3 border-b px-4 py-3 last:border-0 hover:bg-secondary/50">{content}</Link>
  );
}

function CampaignRow({ campaign }: { campaign: Campaign }): React.JSX.Element {
  return (
    <Link
      to="/campaigns/$id"
      params={{ id: campaign.id }}
      className="flex items-center gap-3 border-b px-4 py-3 last:border-0 hover:bg-secondary/50"
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{campaign.name}</span>
      <StatusBadge status={campaign.status} />
      <span className="text-xs tabular-nums text-muted-foreground">v{campaign.published_bundle_version ?? '—'}</span>
    </Link>
  );
}
